import { readonly, ref } from "vue";

// Lowercase because we look it up case-insensitively, but normalize for clarity.
const BUILD_VERSION_HEADER = "x-buttondown-build";

// The build this tab booted on, captured from the first API response that
// carries the header (stamped by emails/middlewares/build_version.py). Once a
// later response advertises a different build, a deploy has shipped underneath
// us and this bundle is potentially stale — continuing to write can trip the
// strict API's `extra="forbid"` 422s, so we prompt a refresh instead.
let bootBuild: string | null = null;

const newBuildAvailable = ref(false);

export const isNewBuildAvailable = readonly(newBuildAvailable);

// Circuit breaker for the whole stale-bundle genre: the refresh banner *and*
// the "please refresh" 422 message swap in OkapiError. App.vue keeps this in
// sync with the `disable_stale_bundle_detection` flag (see
// internal-docs/engineering/best-practices/frontend.md). Newly
// loaded tabs read it straight from `account.flags`; tabs already open when the
// flag flips are covered by the backend withholding the build header instead.
const staleBundleDetectionDisabled = ref(false);

export const isStaleBundleDetectionDisabled = readonly(
  staleBundleDetectionDisabled
);

export const setStaleBundleDetectionDisabled = (disabled: boolean): void => {
  staleBundleDetectionDisabled.value = disabled;
};

export const observeBuildVersion = (response: Response): void => {
  if (staleBundleDetectionDisabled.value) {
    return;
  }
  const build = response.headers.get(BUILD_VERSION_HEADER);
  if (!build) {
    return;
  }
  if (bootBuild === null) {
    bootBuild = build;
    return;
  }
  if (build !== bootBuild) {
    newBuildAvailable.value = true;
  }
};

// Module state is a process-wide singleton (a deploy is global, not
// per-component). Stories/tests that drive `observeBuildVersion` must reset it
// on teardown so a flipped flag doesn't bleed into later cases.
export const resetBuildVersionTracking = (): void => {
  bootBuild = null;
  newBuildAvailable.value = false;
  staleBundleDetectionDisabled.value = false;
};
